import { CONFIRM_DEADLINE_DAYS } from "../constants";
import { parseBRParts } from "./timezone";

// ─── STRING / FORMAT ──────────────────────────────────────────────────────────

/** Title-case a string */
export function tc(s) {
  if (!s) return s;
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format a date string to pt-BR short date + time */
export function fmtDate(d) {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch (e) {
    return d;
  }
}

/** Return "First L." abbreviated name */
export function shortName(name) {
  const parts = name.split(" ");
  if (parts.length === 1) return name;
  return parts[0] + " " + parts[parts.length - 1][0] + ".";
}

// ─── PYRAMID HELPERS ─────────────────────────────────────────────────────────

/** Return the row index for a given position in a pyramid rows array */
export function getRowOfPos(pos, rows) {
  for (let r = 0; r < rows.length; r++) {
    const [a, b] = rows[r];
    if (pos >= a && pos <= b) return r;
  }
  return rows.length - 1;
}

/** Whether a challenger at position cp can challenge a target at tp */
export function canChallenge(cp, tp, rows, maxRowsUp = 1) {
  if (cp <= tp) return false;
  const cr = getRowOfPos(cp, rows);
  const tr = getRowOfPos(tp, rows);
  const rowDiff = cr - tr;

  // Mesma linha: só à esquerda (posição menor = ranking melhor)
  if (rowDiff === 0) return tp < cp;

  // Linha imediatamente acima: todos os jogadores
  if (rowDiff === 1) return true;

  // Duas linhas acima: somente o card diretamente acima ou à direita
  // Fórmula: offset = (tamanhoMinhaLinha - tamanhoLinhaAlvo) / 2
  // O alvo é elegível se colunaAlvo >= minhaColuna - offset
  if (rowDiff === 2 && maxRowsUp >= 2) {
    const myCol      = cp - rows[cr][0];
    const mySize     = rows[cr][1] - rows[cr][0] + 1;
    const targetSize = rows[tr][1] - rows[tr][0] + 1;
    const offset     = (mySize - targetSize) / 2;
    const colAlvoMin = myCol - offset;
    const tcCol      = tp - rows[tr][0];
    return tcCol >= colAlvoMin;
  }

  return false;
}

// ─── PLAYER STATUS ────────────────────────────────────────────────────────────

/** Returns "inativo" | "bloqueado" | null for a player */
export function getBlockStatus(p) {
  if (p.inactiveUntil && new Date(p.inactiveUntil) > new Date()) return "inativo";
  if (p.blockedAsChallenged && new Date(p.blockedAsChallenged) > new Date())
    return "bloqueado";
  return null;
}

/** Returns true if the player is blocked from issuing challenges */
export function isChallengerBlocked(p) {
  if (p.inactiveUntil && new Date(p.inactiveUntil) > new Date()) return true;
  if (p.blockedAsChallenger && new Date(p.blockedAsChallenger) > new Date()) return true;
  return false;
}

/** Returns "ambos" | "desafiante" | "desafiado" | null */
export function getChallengeStatus(p, challenges) {
  const active = challenges.filter((c) => {
    if (c.status === "aguardando_confirmacao") {
      if (isExpired(c)) return false;
    }
    return true;
  });
  const asChallenger = active.some((c) => c.challengerId === p.id);
  const asChallenged = active.some((c) => c.challengedId === p.id);
  if (asChallenger && asChallenged) return "ambos";
  if (asChallenger) return "desafiante";
  if (asChallenged) return "desafiado";
  return null;
}

// ─── CHALLENGE DEADLINE ───────────────────────────────────────────────────────

/**
 * Dado o momento em que o desafio foi enviado (proposedAt), retorna a data
 * efetiva de início da contagem do prazo — ou seja, o próximo dia útil.
 *
 * Regras:
 *  - Dia útil até 19h  → começa hoje (mesma data)
 *  - Dia útil após 19h → começa no próximo dia útil
 *  - Sábado            → começa na segunda-feira
 *  - Domingo           → começa na segunda-feira
 *
 * Feriados: não implementados ainda (sem lista no app).
 * A data retornada aponta para o início do dia (00:00) em horário de Brasília.
 */
export function getEffectiveStartDate(proposedAt) {
  if (!proposedAt) return new Date();

  const { dow, hour, year, month, day } = parseBRParts(proposedAt);

  const isWeekend = dow === 0 || dow === 6;
  const isAfterCutoff = hour >= 18;

  let addDays = 0;
  if (isWeekend || isAfterCutoff) {
    addDays = 1;
    const nextDow = (dow + addDays) % 7;
    if (nextDow === 6) addDays += 2;      // sábado → segunda
    else if (nextDow === 0) addDays += 1; // domingo → segunda
  }

  // Constrói meia-noite BRT da data de início e retorna como Date UTC
  const pad = (n) => String(n).padStart(2, "0");
  const startIso = `${year}-${pad(month)}-${pad(day)}T00:00:00-03:00`;
  const startDate = new Date(startIso);
  if (addDays > 0) startDate.setUTCDate(startDate.getUTCDate() + addDays);
  return startDate;
}

/**
 * Dias restantes até o prazo de confirmação (pode ser negativo).
 * O prazo começa no próximo dia útil após o envio.
 */
export function daysUntilDeadline(proposedAt) {
  if (!proposedAt) return null;
  const start = getEffectiveStartDate(proposedAt);
  // start = 03:00 UTC do dia de início (= meia-noite BRT)
  // Prazo termina às 23:59 BRT do CONFIRM_DEADLINE_DAYS-ésimo dia
  // 23:59 BRT = 02:59 UTC do dia seguinte
  // Logo: avançar (CONFIRM_DEADLINE_DAYS + 1) dias a partir do início e setar 02:59 UTC
  const deadline = new Date(start);
  deadline.setUTCDate(deadline.getUTCDate() + CONFIRM_DEADLINE_DAYS + 1);
  deadline.setUTCHours(2, 59, 59, 0);
  return Math.ceil((deadline - new Date()) / 86400000);
}

/** True if a challenge's confirmation window has expired */
export function isExpired(challenge) {
  if (challenge.status !== "aguardando_confirmacao") return false;
  return daysUntilDeadline(challenge.proposedAt) <= 0;
}

// ─── INATIVIDADE ──────────────────────────────────────────────────────────────

/**
 * Retorna a penalidade de posição e descrição da faixa para um total
 * de dias de inatividade acumulados no ano-calendário, conforme Capítulo V.
 */
export function getInactivityPenalty(totalDays) {
  if (totalDays <= 30)  return { penalty: 0,  faixa: "I",   label: "Sem penalidade (1–30 dias)" };
  if (totalDays <= 60)  return { penalty: 5,  faixa: "II",  label: "−5 posições (31–60 dias)" };
  if (totalDays <= 90)  return { penalty: 10, faixa: "III", label: "−10 posições (61–90 dias)" };
  if (totalDays <= 120) return { penalty: 15, faixa: "IV",  label: "−15 posições (91–120 dias)" };
  if (totalDays <= 150) return { penalty: 20, faixa: "V",   label: "−20 posições (121–150 dias)" };
  return                       { penalty: 25, faixa: "VI",  label: "−25 posições (151–180 dias)" };
}

/**
 * Retorna a próxima faixa de penalidade (para exibir aviso ao jogador).
 * Retorna null se já estiver na faixa máxima.
 */
export function getNextInactivityThreshold(totalDays) {
  if (totalDays < 30)  return { threshold: 30,  penalty: 5  };
  if (totalDays < 60)  return { threshold: 60,  penalty: 10 };
  if (totalDays < 90)  return { threshold: 90,  penalty: 15 };
  if (totalDays < 120) return { threshold: 120, penalty: 20 };
  if (totalDays < 150) return { threshold: 150, penalty: 25 };
  return null;
}

// ─── PLAYER STATS ─────────────────────────────────────────────────────────────

/**
 * Compute wins / losses / wo stats for a player by name from a results array.
 * Returns { wins, losses, woWins, woLosses, total, winRate }
 */
export function getPlayerStats(playerName, results) {
  const mine = results.filter(
    (r) => r.challengerName === playerName || r.challengedName === playerName
  );
  let wins = 0, losses = 0, woWins = 0, woLosses = 0;
  mine.forEach((r) => {
    const isChallenger = r.challengerName === playerName;
    if (r.type === "wo-challenged") {
      if (isChallenger) woWins++;
      else woLosses++;
    } else if (r.type === "wo-challenger") {
      if (!isChallenger) woWins++;
      else woLosses++;
    } else if (r.winner === "challenger") {
      if (isChallenger) wins++;
      else losses++;
    } else {
      if (!isChallenger) wins++;
      else losses++;
    }
  });
  const total = wins + losses;
  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
  return { wins, losses, woWins, woLosses, total, winRate };
}
